import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Checkbox,
  TextField,
  InputAdornment,
  Stack,
} from "@mui/material";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import SearchIcon from "@mui/icons-material/Search";
import * as services from "./../../services/saleng";
import { useNavigate, useSearchParams } from "react-router-dom";
import { statusColors } from "../../utils/statusColor";
import { formatNumber } from "../../utils/formatNumber";

const CardItem = ({ item, onToggle, onClick, status }) => {
  const latestImage =
    item.images && item.images.length > 0
      ? [...item.images].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        )[0].imageUrl
      : null;

  return (
    <Card
      sx={{
        position: "relative",
        minHeight: { xs: 280, sm: 320, md: 350 },
        width: "100%",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
        boxShadow: 3,
        transition: "0.2s",
        "&:hover": { boxShadow: 6, transform: "scale(1.02)" },
        bgcolor: statusColors(status),
      }}
      onClick={() => onClick(item)}
    >
      {/* รูป */}
      <Box
        sx={{
          width: "100%",
          aspectRatio: "16/9",
          overflow: "hidden",
          bgcolor: "#f5f5f5",
        }}
      >
        {latestImage ? (
          <img
            src={latestImage}
            alt="preview"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ImageNotSupportedIcon fontSize="large" color="disabled" />
          </Box>
        )}
      </Box>

      {/* ข้อมูล */}
      <Box
        sx={{
          p: 1,
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Typography sx={{ flexGrow: 1, minHeight: 24 }}>
          {item?.shipping?.shippingName || "-"}
        </Typography>

        <Typography sx={{ flexGrow: 1, minHeight: 24 }}>
          เลขที่Batch <strong>#{(item.billNumber || "").split("-").pop()}</strong>
        </Typography>

        <Typography sx={{ flexGrow: 1, minHeight: 24 }}>
          {item?.shipping?.note || "-"}
        </Typography>

        <Typography sx={{ flexGrow: 1, minHeight: 24 }}>
          ยอดเงิน{" "}
          <strong>
            {formatNumber(
              item.items?.reduce((sum, i) => sum + (i.netPrice || 0), 0)
            )}
          </strong>{" "}
          บาท
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: "auto", flexGrow: 1, minHeight: 24 }}
        >
          {new Date(item.createdAt).toLocaleString("th-TH", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            timeZone: "Asia/Bangkok",
          })}
        </Typography>
      </Box>

      {/* Badge จำนวนรายการ */}
      <Box
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          bgcolor: "#1976d2",
          color: "white",
          px: 1,
          py: 0.3,
          borderRadius: 1,
          fontSize: 12,
        }}
      >
        {item?.items?.length || 0} รายการ
      </Box>
    </Card>
  );
};

export default function ImageCardGrid() {
  const [isLoading, setIsLoading] = useState(true);
  const [transaction, setTransaction] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");

  const formatDate = (date) => date.toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    if (status !== "payed") date.setMonth(date.getMonth() - 1);
    return formatDate(date);
  });
  const [endDate, setEndDate] = useState(formatDate(new Date()));

  // ---- ดึงข้อมูล ----
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const res = await services.getSaleTransactionByStatus(
          status,
          startDate,
          endDate
        );

        const data = res.data?.data ?? [];
        setTransaction(data);
      } catch (err) {
        console.error("fetch error", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, status]);

  const handleToggle = (id) => {
    setTransaction((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, selected: !item.selected } : item
      )
    );
  };

  const handleTicketSelect = async (data) => {
    navigate(
      `/sale/checkout?id=${data.transactionId}&ticket=${data.billNumber}&status=${data.status}`
    );
  };

  const filtered = transaction?.filter((item) => {
    const query = searchQuery.trim().toLowerCase();
    const id = item.id?.toString() || "";
    const customerName = item.customer?.name || item.customerName || "";
    const bill = item.billNumber || "";
    return (
      id.includes(query) ||
      customerName.toLowerCase().includes(query) ||
      bill.toLowerCase().includes(query)
    );
  });

  return (
    <Box p={2}>
      <AppBreadcrumbs />

      {/* ฟิลเตอร์: responsive row/column */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        my={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        useFlexGap
        flexWrap="wrap"
      >
        <TextField
          label="ค้นหา"
          value={searchQuery}
          placeholder="ค้นหาโดยเลขที่รายการ, ชื่อ, หมายเลข ฯลฯ"
          onChange={(e) => setSearchQuery(e.target.value)}
          size="medium"
          fullWidth
          sx={{ maxWidth: { xs: "100%", sm: 420 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <Stack direction="row" spacing={2} sx={{ width: { xs: "100%", sm: "auto" } }}>
          <TextField
            label="เริ่มต้น"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: 1, minWidth: { xs: "50%", sm: 180 } }}
          />
          <TextField
            label="สิ้นสุด"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ flex: 1, minWidth: { xs: "50%", sm: 180 } }}
          />
        </Stack>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "repeat(1, 1fr)",  // โทรศัพท์
            sm: "repeat(2, 1fr)",  // แท็บเล็ตเล็ก
            md: "repeat(3, 1fr)",  // แท็บเล็ต/เดสก์ท็อปเล็ก
            lg: "repeat(4, 1fr)",  // เดสก์ท็อป
            xl: "repeat(6, 1fr)",  // เดสก์ท็อปใหญ่
          },
          opacity: isLoading ? 0.6 : 1,
          pointerEvents: isLoading ? "none" : "auto",
        }}
      >
        {filtered.map((item) => (
          <Box key={item.id}>
            <CardItem
              item={item}
              onToggle={handleToggle}
              onClick={handleTicketSelect}
              status={status}
            />
          </Box>
        ))}
      </Box>

      {!isLoading && filtered.length === 0 && (
        <Box sx={{ textAlign: "center", color: "text.secondary", py: 6 }}>
          <Typography>ไม่พบรายการตามเงื่อนไขที่ค้นหา</Typography>
        </Box>
      )}
    </Box>
  );
}
